import { HpEither } from '@avantia/logging-and-errors';
import { createQuestionPanelId, ensureIdDoesNotStartWithPrefix, HpHelpInfo } from '@avantia/questionset-model';
import { Form } from 'react-bootstrap';
import {
  HpClientQuestion,
  HpClientStandardButtonTextAndStyle,
  HpClientStandardInputTextAndStyle,
  HpHelpInfoUI,
  HpStringAnswerSource
} from '../../types';
import { HpOnClickEvent, HpOnMouseEvent } from '../../types/inputControlTypes';
import { HpInfoPositionType } from '../../types/widgetUtilitiesTypes';
import { HelpIconWidget, HelpWidget, InfoWidget, WarningWidget } from './helpInfoWidget';
import { HtmlWidget } from './htmlWidget';
import { MessageWidget } from './messageWidget';
import { getHelpAndInfoText } from './widgetUtilities';

export type HpFormGroupClassTypes =
  | 'button'
  | 'checkbox-list'
  | 'dropdown'
  | 'image-checkbox-list'
  | 'image-radio-button-list'
  | 'radio-button-list'
  | 'switch'
  | 'text-input'
  | 'auto-complete'
  | 'date-picker'
  | 'date-drop-down'
  | 'read-only-text'
  | 'section-validation';

export interface HpFormGroupProps {
  answerSource: HpStringAnswerSource;
  checkBoxLabelText?: string | undefined;
  control: JSX.Element | JSX.Element[];
  controlType: HpFormGroupClassTypes;
  createFormGroupCustomWidget?: HpCreateFormGroupCustomWidget;
  dataType: string | null;
  error: string | undefined;
  errorPosition?: HpErrorPositionTypes;
  helpInfo?: HpHelpInfo;
  helpInfoUI?: HpHelpInfoUI;
  id: string;
  onClick?: HpOnClickEvent;
  question: HpClientQuestion;
  questionText: string | undefined;
  subtext: string | undefined;
  textAndStyle: HpClientStandardButtonTextAndStyle | HpClientStandardInputTextAndStyle;
}

export type HpErrorPositionTypes = 'bottom' | 'top' | undefined;

export const SuccessMessagePrefix = 'SUCCESS:';

//TODO: PR Refactor widget implementation
export function StandardFormGroupWidget({
  answerSource,
  control,
  controlType,
  createFormGroupCustomWidget,
  dataType,
  error,
  errorPosition,
  helpInfo,
  helpInfoUI,
  id,
  onClick,
  question,
  questionText,
  subtext,
  textAndStyle
}: HpFormGroupProps): JSX.Element {
  ensureIdDoesNotStartWithPrefix(id);
  const { cssClass } = textAndStyle;
  const data = getHelpAndInfoText(helpInfo, helpInfoUI);
  const {
    canShowHelp,
    canShowInfo,
    canShowWarning,
    helpText,
    infoPosition,
    infoText,
    isHelpVisible,
    isInfoIconHidden,
    warningText
  } = data;
  const classNames: string[] = ['hp-standard', `hp-${controlType}`, `hp-answer-${answerSource}`];
  const { errorPosition: errPos, errorWidget, hasError } = errorMessageHelper(error, errorPosition);
  errorPosition = errPos;

  if (dataType) {
    classNames.push(`hp-data-${dataType}`);
  }

  if (hasError) {
    classNames.push('av-input-error');
  }

  if (cssClass) {
    classNames.push(cssClass);
  }

  const renderProps: HpCreateFormGroupCustomWidgetProps = {
    panelId: createQuestionPanelId(id),
    classNames,
    control,
    errorPosition,
    errorWidget,
    helpWidget: canShowHelp && isHelpVisible ? <HelpWidget helpText={helpText as string} /> : null,
    infoPosition,
    infoWidget: canShowInfo ? <InfoWidget infoText={infoText as string} isInfoIconHidden={isInfoIconHidden} /> : null,
    questionLabel: createQuestionLabel({
      questionText,
      canShowHelp,
      onHelpClick: onClick,
      question
    }),
    subtext: subtext ? <HtmlWidget html={subtext} tagName="sub" /> : null,
    warningWidget: canShowWarning ? <WarningWidget warningText={warningText as string} /> : null
  };

  const renderer: HpCreateFormGroupCustomWidget = createFormGroupCustomWidget || createFormGroupStandardWidget;
  return renderer(renderProps);
}

export type HpCreateFormGroupCustomWidget = (props: HpCreateFormGroupCustomWidgetProps) => JSX.Element;

export interface HpCreateFormGroupCustomWidgetProps {
  classNames: string[];
  control: JSX.Element | JSX.Element[];
  errorPosition: 'top' | 'bottom' | undefined;
  errorWidget: JSX.Element | null;
  helpWidget: JSX.Element | null;
  infoPosition: HpInfoPositionType | undefined;
  infoWidget: JSX.Element | null;
  panelId: string;
  questionLabel: JSX.Element | null;
  subtext: JSX.Element | null;
  warningWidget: JSX.Element | null;
}

function createFormGroupStandardWidget({
  classNames,
  control,
  errorPosition,
  errorWidget,
  helpWidget,
  infoPosition,
  infoWidget,
  panelId,
  questionLabel,
  subtext,
  warningWidget
}: HpCreateFormGroupCustomWidgetProps): JSX.Element {
  return (
    <Form.Group id={panelId} className={classNames.join(' ')}>
      {infoPosition === 'before' && infoWidget}
      {errorPosition !== 'bottom' && errorWidget}
      {questionLabel}
      {subtext}
      {infoPosition === 'top' && infoWidget}
      {helpWidget}
      <div className="av-field-section">{control}</div>
      {errorPosition === 'bottom' && errorWidget}
      {infoPosition === 'bottom' && infoWidget}
      {warningWidget}
    </Form.Group>
  );
}

export interface HpCreateQuestionLabel {
  interactiveIcon?: JSX.Element;
  questionText: string | undefined;
}

export interface HpCreateQuestionLabelWithHelpIcon extends HpCreateQuestionLabel {
  canShowHelp: boolean;
  onHelpClick: HpOnMouseEvent | undefined;
  question: HpClientQuestion;
}

export function createQuestionLabel({
  canShowHelp,
  onHelpClick,
  interactiveIcon,
  question,
  questionText
}: HpEither<HpCreateQuestionLabel, HpCreateQuestionLabelWithHelpIcon>): JSX.Element | null {
  if (!questionText) {
    return null;
  }

  return (
    <Form.Label tabIndex={0}>
      <HtmlWidget html={questionText} />
      {interactiveIcon}
      {onHelpClick && canShowHelp && question && (
        <HelpIconWidget onClick={onHelpClick} question={question}></HelpIconWidget>
      )}
    </Form.Label>
  );
}

function errorMessageHelper(error: string | undefined, errorPosition?: 'bottom' | 'top' | undefined) {
  let hasError = false;
  let errorWidget: JSX.Element | null = null;
  if (error) {
    if (error.startsWith(SuccessMessagePrefix)) {
      error = error.substring(SuccessMessagePrefix.length);
      errorPosition ||= 'bottom';
    } else {
      hasError = true;
    }

    errorWidget = <MessageWidget message={error} type={hasError ? 'error' : 'success'} />;
  }

  return { hasError, errorWidget, errorPosition };
}
